<template>
  <div class="data-grid" v-if="hasRows">
    <table
      class="uk-table uk-table-divider uk-table-justify"
      :class="tableClass"
    >
      <thead class="data-grid__header">
        <tr class="data-grid__header-row">
          <th
            v-if="selectable"
            class="data-grid__header-cell--selector uk-table-shrink"
          >
            <input
              v-if="multiple"
              type="checkbox"
              class="uk-checkbox"
              ref="toggleAllCheckbox"
              :checked="allRowsSelected"
              :indeterminate.prop="someRowsSelected"
              @click="toggleAllSelected"
            >
          </th>
          <slot name="beforeHead" />
          <th
            v-if="isSortableField(field)"
            v-for="field in fields"
            class="data-grid__header-cell data-grid__header-cell--sortable"
            @click="sortBy(field)"
          >
            {{columns[field]}}
            <span v-if="sortField == field" class="data-grid__sort-indicator">
              <icon v-if="sortDirection == 'desc'" name="chevron-down" />
              <icon v-else name="chevron-up" />
            </span>
          </th>
          <th v-else class="data-grid__header-cell">
            {{columns[field]}}
          </th>
          <th v-if="showActions"></th>
        </tr>
      </thead>
      <tbody
        v-if="hasRows"
        class="data-grid__body"
        :class="{ 'data-grid__body--loading': isLoading }"
      >
        <tr v-for="row in rows" class="data-grid__row">
          <slot name="row" :fields="fields" :row="row">
            <td
              v-if="selectable"
              class="data-grid__cell--selector uk-table-shrink"
            >
              <input
                v-if="multiple"
                type="checkbox"
                class="uk-checkbox"
                :checked="isSelectedRow(row)"
                @click="toggleSelected(row)"
              >
              <input
                v-else
                type="radio"
                class="uk-radio"
                :checked="isSelectedRow(row)"
                @click="selectExclusively(row)"
              >
            </td>
            <slot name="beforeRow" :row="row" />
            <td v-for="field in fields" class="data-grid__cell">
              <slot :name="field" :row="row" :value="row[field]">
                {{row[field]}}
              </slot>
            </td>
            <td v-if="showActions" class="data-grid__cell">
              <slot name="actions" :row="row">
                <data-grid-actions
                  class="data-grid__actions"
                  :editable="editable"
                  :removable="removable"
                  @edit="$emit('edit', row)"
                  @remove="$emit('remove', row)"
                />
              </slot>
            </td>
          </slot>
        </tr>
      </tbody>
    </table>

    <paginator
      v-if="pageInfo"
      :change-route="changeRoute"
      @change-page="changePage"
      class="data-grid__paginator"
      :page-info="pageInfo"
    />
  </div>

  <div v-else class="data-grid data-grid--empty uk-placeholder uk-text-center">
    <icon name="bolt" class="uk-margin-small-right" />
    <slot name="empty">{{$t('hints.empty')}}</slot>
  </div>
</template>

<script>
import { camelCase, difference, includes, intersection, without } from 'lodash';
import CommonButton from '@/components/CommonButton';
import DataGridActions from '@/components/DataGridActions';
import Paginator from '@/components/Paginator';

export default {
  name: 'data-grid',
  components: {
    CommonButton,
    DataGridActions,
    Paginator,
  },
  props: {
    columns: {
      type: Object,
      default: () => {},
    },
    sortable: Array,
    filterable: Array,
    rows: {
      type: Array,
      default: () => [],
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    sortInfo: {
      type: Object,
      default: () => ({ field: null, direction: null }),
    },
    pageInfo: Object,
    showActions: {
      type: Boolean,
      default: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    tableClass: String,
    selectable: Boolean,
    multiple: Boolean,
    editable: {
      type: Boolean,
      default: true,
    },
    removable: {
      type: Boolean,
      default: true,
    },
    changeRoute: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      internalSelectedRows: this.selectedRows || [],
    };
  },
  watch: {
    rows(rows) {
      this.internalSelectedRows = intersection(this.internalSelectedRows, rows);
      if (this.selectable) this.onChangeSelection();
    },
    selectedRows(selectedRows) {
      this.internalSelectedRows = intersection(this.rows, selectedRows);
      if (this.selectable) this.onChangeSelection();
    },
  },
  computed: {
    isSortable() {
      return !!this.sortInfo;
    },
    fields() {
      return Object.keys(this.columns);
    },
    filterableFields() {
      if (this.filterable && this.filterable.length) return this.filterable;
      return [];
    },
    sortableFields() {
      if (this.sortable && this.sortable.length) return this.sortable;
      return [];
    },
    sortField() {
      return camelCase(this.sortInfo.field);
    },
    sortDirection() {
      return camelCase(this.sortInfo.direction);
    },
    columnsCount() {
      let count = this.fields.length;
      if (this.showActions) count += 1;
      return count;
    },
    hasRows() {
      return this.rows.length > 0;
    },
    allRowsSelected() {
      return this.internalSelectedRows.length === this.rows.length;
    },
    someRowsSelected() {
      const selectedCount = this.internalSelectedRows.length;
      return selectedCount > 0 && selectedCount < this.rows.length;
    },
    addedRows() {
      return difference(this.internalSelectedRows, this.selectedRows);
    },
    removedRows() {
      return difference(this.selectedRows, this.internalSelectedRows);
    },
  },
  methods: {
    changePage(queryParams) {
      if (!this.changeRoute) {
        this.$emit('change-page', queryParams);
      }
    },
    isSortableField(field) {
      return this.isSortable && includes(this.sortableFields, field);
    },
    isFilterableField(field) {
      return includes(this.filterableFields, field);
    },
    sortBy(field) {
      let direction = 'asc';
      if (this.sortField === field && this.sortDirection === 'asc') {
        direction = 'desc';
      }
      this.$emit('sort', { field, direction });
    },
    filterBy(field) {
      this.$emit('filter', field);
    },
    isSelectedRow(row) {
      return includes(this.internalSelectedRows, row);
    },
    toggleAllSelected() {
      if (!this.allRowsSelected) {
        this.selectAll();
      } else {
        this.unselectAll();
      }
    },
    toggleSelected(row) {
      if (this.isSelectedRow(row)) {
        this.unselect(row);
      } else {
        this.select(row);
      }
    },
    onChangeSelection() {
      this.$emit('change-selection', {
        selectedRows: this.internalSelectedRows,
        addedRows: this.addedRows,
        removedRows: this.removedRows,
      });
    },
    selectAll() {
      this.internalSelectedRows = this.rows;
      this.$emit('select-all', {
        rows: this.rows,
        selectedRows: this.internalSelectedRows,
        addedRows: this.addedRows,
        removedRows: this.removedRows,
      });
      this.onChangeSelection();
    },
    unselectAll() {
      const uninternalSelectedRows = this.internalSelectedRows.slice(0);
      this.internalSelectedRows = [];
      this.$emit('unselect-all', {
        rows: uninternalSelectedRows,
        selectedRows: this.internalSelectedRows,
        addedRows: this.addedRows,
        removedRows: this.removedRows,
      });
      this.onChangeSelection();
    },
    selectExclusively(row) {
      this.internalSelectedRows = [row];
      this.$emit('select', {
        row,
        selectedRows: this.internalSelectedRows,
        addedRows: this.addedRows,
        removedRows: this.removedRows,
      });
      this.onChangeSelection();
    },
    select(row) {
      this.internalSelectedRows.push(row);
      this.$emit('select', {
        row,
        selectedRows: this.internalSelectedRows,
        addedRows: this.addedRows,
        removedRows: this.removedRows,
      });
      this.onChangeSelection();
    },
    unselect(row) {
      this.internalSelectedRows = without(this.internalSelectedRows, row);
      this.$emit('unselect', {
        row,
        selectedRows: this.internalSelectedRows,
        addedRows: this.addedRows,
        removedRows: this.removedRows,
      });
      this.onChangeSelection();
    },
  },
  i18n: {
    messages: {
      de: {
        hints: {
          empty: 'Keine Einträge gefunden.',
        },
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import '../theme/variables.scss';

.data-grid__header-cell--sortable {
  cursor: pointer;
}

.data-grid__actions {
  float: right;
}

.data-grid__body tr td {
  opacity: 1;
}

.data-grid__body--loading tr td {
  opacity: 0.3;
}

.data-grid__filter {
  border: none;
  padding: none;
  background: none;
  outline: none;
  color: $icon-link-color;

  &:hover, &:focus {
    color: $icon-link-hover-color;
  }

  &:active {
    color: $icon-link-active-color;
  }
}
</style>



// WEBPACK FOOTER //
// DataGrid.vue?f45e1c2a